import Mixpanel from 'mixpanel-browser';
import {
  MIXPANEL_TOKENS,
  SESSION_PROPERTIES,
  ENVIRONMENT_KEYS,
  ANALYTICS_TO_TRACK_VIA_API,
} from 'shared/constants';
import { currentEnvironment } from 'shared/services/Environment';
import AnalyticsAPI from 'shared/api/analytics.service.api';
import analyticsSession from './analytics-session';
import { getDeviceId } from './device-fingerprint';

class AnalyticsService {
  constructor() {
    // Session properties to be sent with every event
    this.sessionProperties = {
      [SESSION_PROPERTIES.CLIENT_APP]: 'widget',
    };
  }

  async init() {
    Mixpanel.init(MIXPANEL_TOKENS[currentEnvironment()]);

    analyticsSession.init();

    // Initialize device fingerprint and register as session property
    const deviceId = await getDeviceId();
    if (deviceId) {
      this.sessionProperties[SESSION_PROPERTIES.DEVICE_ID] = deviceId;
      Mixpanel.register({ [SESSION_PROPERTIES.DEVICE_ID]: deviceId });
      console.debug('[Analytics] Device ID registered:', deviceId);
    }
  }

  track(eventName, properties = {}, options = {}) {
    // Update activity on every track call
    analyticsSession.updateActivity();

    const eventData = {
      ...properties,
      ...this.sessionProperties,
      [SESSION_PROPERTIES.SESSION_ID]: analyticsSession.getSessionId(),
    };

    console.debug(`[Analytics] Track event - ${eventName}`, eventData);

    // Killer switch
    if (currentEnvironment() === ENVIRONMENT_KEYS.DEVELOPMENT) return false;

    Mixpanel.track(eventName, eventData);

    if (ANALYTICS_TO_TRACK_VIA_API.includes(eventName)) {
      AnalyticsAPI.trackEvent({ event: `${eventName} {proxy}`, properties: eventData });
    }
  }

  reset() {
    console.debug('[Analytics] Reset analytics service');

    // Clear stored session data
    analyticsSession.destroy();

    // Killer switch
    if (currentEnvironment() === ENVIRONMENT_KEYS.DEVELOPMENT) return false;

    // Reset mixpanel identity
    Mixpanel.reset();
  }

  setUserId(id) {
    console.debug(`[Analytics] Set user id - ${id}`);

    // Killer switch
    if (currentEnvironment() === ENVIRONMENT_KEYS.DEVELOPMENT) return false;

    Mixpanel.identify(id);
  }

  registerUserData(data) {
    console.debug(`[Analytics] Set user data`, data);

    // Killer switch
    if (currentEnvironment() === ENVIRONMENT_KEYS.DEVELOPMENT) return false;

    Mixpanel.register_once(data);
  }

  setGroup({ key, value }) {
    console.debug(`[Analytics] Set group ${key} - ${value}`);

    // Killer switch
    if (currentEnvironment() === ENVIRONMENT_KEYS.DEVELOPMENT) return false;

    Mixpanel.set_group(key, value);
    this.sessionProperties[key] = value;
    Mixpanel.register_once(this.sessionProperties);
  }

  setSessionProperty(key, value) {
    this.sessionProperties[key] = value;
  }

  // Cleanup method for when the service is destroyed
  destroy() {
    // Killer switch
    if (currentEnvironment() === ENVIRONMENT_KEYS.DEVELOPMENT) return false;

    analyticsSession.destroy();
  }
}

export default new AnalyticsService();
